import React, { useState, useMemo, useEffect } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import { Tabs, Select, Input, message, Button, Tooltip, Row, Col, Form, Drawer, Tag } from "antd";
import { FilterOutlined } from "@ant-design/icons";
import axios from "axios";
import moment from "moment";
import CustomerMainLayout from "../CustomerMainLayout";
import ReviewImageCard from "./ReviewImageCard";
import ResolutionRadioButtons from "../TwoDImagesPage/ResolutionRadioButtons";
import TwoDImagesContext from "../../ContextFiles/TwoDImagesContext";
import { REVIEW_IMAGES_BATCH, getBaseURL, GET_COMPANY_USERS,CREATE_COLLABORATE_ENTITY, REVIEW_IMAGE_CONTROLLER } from "../../../../environments/env";
import AiLoader from "../../AiAsserCreate/AiLoader";
import "./ReviewImagesPage.scss";

const ReviewAcceptedIcon = "/img/ai_creator/review_accepted.svg";
const ReviewRejectedIcon = "/img/ai_creator/review_rejected.svg";
const ReviewPendingIcon = "/img/ai_creator/review_pending.svg";

const { TabPane } = Tabs;
const { Search } = Input;

const ReviewImagesPage = ({ history, location }) => {
  // Get initial tab from URL params or default to "pending"
  const getInitialTab = () => {
    const params = new URLSearchParams(location.search);
    const tabFromUrl = params.get('tab');
    const validTabs = ['pending', 'approved', 'rejected'];
    return validTabs.includes(tabFromUrl) ? tabFromUrl : 'pending';
  };

  const [selectedOwner, setSelectedOwner] = useState("all");
  const [selectedResolution, setSelectedResolution] = useState("all");
  const [tabResolutions, setTabResolutions] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState(getInitialTab());
  const [allImages, setAllImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentUserRole, setCurrentUserRole] = useState(null);
  const [isFilterModal, setIsFilterModal] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const currentUsername = localStorage.getItem('username');

  // Fetch company users to get current user's role
  useEffect(() => {
    const fetchCompanyUsers = async () => {
      try {
        const companyId = localStorage.getItem('company_id');
        const currentUsername = localStorage.getItem('username');

        if (!companyId || !currentUsername) {
          return;
        }

        const response = await axios.post(GET_COMPANY_USERS, {
          company_id: companyId
        });

        if (response.data && response.data.users) {
          const currentUser = response.data.users.find(
            user => user.username === currentUsername
          );

          if (currentUser) {
            setCurrentUserRole(currentUser.role);
          }
        }
      } catch (err) {
        console.error('Error fetching company users:', err);
      }
    };

    fetchCompanyUsers();
  }, []);

  // Fetch images from API
  const fetchReviewImages = async () => {
    try {
      setLoading(true);
      setError(null);

      const customerUsername = localStorage.getItem('username');
      const companyId = localStorage.getItem('company_id');

      if (!customerUsername && !companyId) {
        throw new Error('User credentials not found');
      }

      // Build filter string
      let filterParts = [];
      if (customerUsername) {
        filterParts.push(`customer_username__exact='${customerUsername}'`);
      }
      if (companyId) {
        filterParts.push(`company_id__exact=${companyId}`);
      }

      const filterString = filterParts.join('||');

      const payload = {
        required_fields: [
          "image_id",
          "review_status",
          "submitted_for_review_on",
          "product_id",
          "image_uri",
          "customer_username",
          "company_id",
          "submitted_for_review_on_stamp",
          "collab_id",
          "is_final_image",
          "width",
          "height",
          "display_name",
          // "rejection_reason",
          // "reviewed_on",
          "submitted_for_review_by",
          "prompt_text",
          "flux_prompt",
          "inspiration_image_uri"
        ],
        filter_string: `(${filterString})`,
        order_by: "submitted_for_review_on_stamp desc"
      };

      const response = await axios.post(REVIEW_IMAGES_BATCH, payload);

      // Transform API response to match component structure
      const transformedImages = response.data.map(item => ({
        id: item.image_id,
        title: item.display_name || 'Untitled',
        status: item.review_status, // pending, approved, rejected
        created_by: item.customer_username,
        thumbnail: `${getBaseURL()}${item.image_uri}`,
        image_uri: item.image_uri,
        collab_id: item.collab_id,
        width: item.width || 2048,
        height: item.height || 2048,
        product_id: item.product_id,
        company_id: item.company_id,
        submitted_on: item.submitted_for_review_on,
        submitted_on_stamp: item.submitted_for_review_on_stamp,
        prompt_text: item.prompt_text,
        flux_prompt: item.flux_prompt,
        submitted_for_review_by: item.submitted_for_review_by,
        // reviewed_on: item.reviewed_on,
        // rejection_reason: item.rejection_reason,
        inspiration_image_uri: item.inspiration_image_uri,
        is_final: item.is_final_image,
      }));

      setAllImages(transformedImages);
    } catch (err) {
      console.error('Error fetching review images:', err);
      setError(err.message || 'Failed to fetch review images');
      message.error('Failed to load review images');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReviewImages();
  }, []);

  // Update URL params when activeTab changes
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const currentTab = params.get('tab');
    
    if (currentTab !== activeTab) {
      params.set('tab', activeTab);
      history.replace({
        pathname: location.pathname,
        search: params.toString(),
      });
    }
  }, [activeTab, history, location.pathname, location.search]);

  // Get unique owners (distinct usernames)
  const distinctUsernames = useMemo(() => {
    const uniqueOwners = new Set(allImages.map(img => img.created_by).filter(Boolean));
    return Array.from(uniqueOwners).sort();
  }, [allImages]);

  const getResolutionCategory = (image) => {
    if (image.height && image.width) {
      const maxDimension = Math.max(image.height, image.width);
      if (maxDimension <= 1024) return '1k';
      else if (maxDimension <= 2048) return '2k';
      else if (maxDimension <= 4096) return '4k';
      else return 'custom';
    }
    return 'all';
  };

  const getTabResolution = (tabName) => {
    return tabResolutions[tabName] || "all";
  };

  // Filter images based on criteria
  const filteredImages = useMemo(() => {
    return allImages.filter(img => {
      // Filter by status/tab
      if (activeTab === 'pending' && img.status !== 'pending') return false;
      if (activeTab === 'approved' && img.status !== 'approved') return false;
      if (activeTab === 'rejected' && img.status !== 'rejected') return false;

      // Filter by owner
      if (selectedOwner !== 'all' && img.created_by !== selectedOwner) return false;

      // Filter by resolution (tab-specific)
      const tabResolution = getTabResolution(activeTab);
      if (tabResolution !== 'all') {
        const imgResolutionCategory = getResolutionCategory(img);
        if (imgResolutionCategory !== tabResolution) return false;
      }

      // Filter by search term
      if (searchTerm && !img.title.toLowerCase().includes(searchTerm.toLowerCase())) return false;

      // Filter by date range
      if (startDate && endDate) {
        if (!img.submitted_on) {
          return false;
        }

        // Try parsing the date with multiple formats
        let imgDate = moment(img.submitted_on);
        if (!imgDate.isValid()) {
          imgDate = moment(img.submitted_on, "YYYY-MM-DD");
        }
        if (!imgDate.isValid()) {
          imgDate = moment(img.submitted_on, "MM/DD/YYYY");
        }
        if (!imgDate.isValid()) {
          imgDate = moment(img.submitted_on, "M/D/YYYY");
        }

        if (!imgDate.isValid()) {
          return false;
        }

        const filterStartDate = moment(startDate, "M/D/YYYY").startOf('day');
        const filterEndDate = moment(endDate, "M/D/YYYY").endOf('day');

        if (!imgDate.isBetween(filterStartDate, filterEndDate, null, '[]')) return false;
      }

      return true;
    });
  }, [allImages, activeTab, selectedOwner, searchTerm, tabResolutions, startDate, endDate]);

  // Get counts for each tab
  const counts = useMemo(() => {
    return {
      pending: allImages.filter(img => img.status === 'pending').length,
      approved: allImages.filter(img => img.status === 'approved').length,
      rejected: allImages.filter(img => img.status === 'rejected').length,
    };
  }, [allImages]);

  const handleCollaborate = async (image) => {
    console.log('Collaborate on image:', image);
    
    const userName = localStorage.getItem('username')

    const payload = {
      image_id: image.id,
      display_name: image.display_name || 'Untitled',
      camera_name: image.image_uri.split('/').pop(),
      thumbnail: image.image_uri.split('/').pop().replace(/(\.[^.]+)$/, "_512$1"),
      created_by: userName,
      last_modified_by: userName,
      state:{}
    }
    console.log('payload',payload)

    const res = axios.post(CREATE_COLLABORATE_ENTITY, payload)

    console.log('res', res)
  
  };

  const handleViewRejectionHistory = (image) => {
    console.log('View rejection history:', image);
    // Add modal or navigation logic here
  };

  const handleRemoveFromReview = (image) => {
    console.log('Remove from review:', image);
    // Add removal logic here
    let payload = {
      "action" : "remove_from_review",
      "image_id": image.id
    }
    if (image.collab_id){
      payload.collab_id = image.collab_id;
    }
    console.log('payload',payload)
    axios.post(REVIEW_IMAGE_CONTROLLER, payload).then(res => {
      if(res.status === 200){
        message.success('Image removed from review');
        fetchReviewImages();
      }
    })
  };

  const onChangeTabResolution = (tabName, newResolution) => {
    const updatedTabResolutions = {
      ...tabResolutions,
      [tabName]: newResolution
    };
    setTabResolutions(updatedTabResolutions);
  };

  // Filter handlers
  const handleFilterModalClose = () => {
    setIsFilterModal(false);
  };

  const handleFilterOpen = () => {
    setIsFilterModal(true);
  };

  const handleDateFilterChange = (value) => {
    const today = moment();
    let newStartDate = "";
    let newEndDate = "";

    switch (value) {
      case "last_30_days":
        newStartDate = today.clone().subtract(30, "days").format("M/D/YYYY");
        newEndDate = today.format("M/D/YYYY");
        break;
      case "month":
        newStartDate = today.clone().startOf("month").format("M/D/YYYY");
        newEndDate = today.format("M/D/YYYY");
        break;
      case "last_month":
        newStartDate = today.clone().subtract(1, "month").startOf("month").format("M/D/YYYY");
        newEndDate = today.clone().subtract(1, "month").endOf("month").format("M/D/YYYY");
        break;
      case "last_6_month":
        newStartDate = today.clone().subtract(6, "months").startOf("month").format("M/D/YYYY");
        newEndDate = today.format("M/D/YYYY");
        break;
      case "year":
        newStartDate = today.clone().startOf("year").format("M/D/YYYY");
        newEndDate = today.format("M/D/YYYY");
        break;
      case "last_year":
        newStartDate = today.clone().subtract(1, "year").startOf("year").format("M/D/YYYY");
        newEndDate = today.clone().subtract(1, "year").endOf("year").format("M/D/YYYY");
        break;
      case "all":
        newStartDate = "";
        newEndDate = "";
        break;
      default:
        break;
    }

    setStartDate(newStartDate);
    setEndDate(newEndDate);
  };

  const clearDateRange = () => {
    setStartDate("");
    setEndDate("");
  };

  // Create context value for ResolutionRadioButtons
  const contextValue = {
    onChangeTabResolution,
    getTabResolution,
  };

  if (loading) {
    return (
      <CustomerMainLayout selected="31">
        <div className="review-images-page" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 'calc(100vh - 200px)', width: '100%' }}>
          <AiLoader />
        </div>
      </CustomerMainLayout>
    );
  }

  if (error) {
    return (
      <CustomerMainLayout selected="31">
        <div className="review-images-page" style={{ padding: '40px', textAlign: 'center' }}>
          <h2>Error Loading Images</h2>
          <p>{error}</p>
        </div>
      </CustomerMainLayout>
    );
  }

  return (
    <CustomerMainLayout selected="31">
      <TwoDImagesContext.Provider value={contextValue}>
        <div className="review-images-page">
          {/* Header Row */}
          <Row
            gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}
            style={{ marginBottom: 8, marginTop: 16 }}
          >
            <Col
              lg={14}
              xl={16}
              className="justify-in-start"
              style={{ display: "block", marginTop: 10 }}
            >
              <div className="justify-in-start">
                <div className="justify-in-start" style={{ marginRight: 12 }}>
                  <Tooltip
                    title={<span className="manrope f-12 white">Review Images</span>}
                  >
                    <span>
                      <div className="manrope f-24 black-33 clamp-text-400 w-600">
                        <span>Review Images</span>
                      </div>
                    </span>
                  </Tooltip>
                </div>
              </div>
            </Col>
            <Col
              lg={10}
              xl={8}
              className="gutter-row justify-in-end"
              style={{ display: "block", marginTop: 10 }}
            >
              <div className="justify-in-end">
                <Select
                  value={selectedOwner !== undefined ? (selectedOwner || "all") : undefined}
                  onChange={(value) => {
                    setSelectedOwner(value === "all" ? "all" : value);
                  }}
                  placeholder="Created by username"
                  style={{
                    width: 180,
                    height: 40,
                    marginRight: 8
                  }}
                  className="ai-images-dropdown"
                  optionLabelProp="label"
                >
                  <Select.Option value="all" label="Created by All">
                    All
                  </Select.Option>
                  {distinctUsernames.sort((a, b) => {
                    if (a === currentUsername) return -1;
                    if (b === currentUsername) return 1;
                    return a.localeCompare(b);
                  }).map((username) => (
                    <Select.Option
                      key={username}
                      value={username}
                      label={`Created by ${username === currentUsername ? 'You' : username}`}
                    >
                      {username === currentUsername ? 'You' : username}
                    </Select.Option>
                  ))}
                </Select>
                <Form>
                  <Input
                    style={{ width: 320 }}
                    placeholder="Search all images"
                    className="manrope f-14 d-inline mat-search-bar mr-8"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </Form>
                <Tooltip title="Filter Images" placement="top">
                  <Button
                    className="icon-button-container mr-8"
                    onClick={handleFilterOpen}
                  >
                    <FilterOutlined />
                  </Button>
                </Tooltip>
              </div>
            </Col>
          </Row>

          {/* Show applied filters */}
          {(startDate && endDate) && (
            <Row style={{ marginBottom: 10, marginTop: 10, paddingLeft: 20 }}>
              <Col span={24}>
                <h4 className="manrope f-14 black-55 italic">Showing results for</h4>
                <div>
                  {startDate && endDate && (
                    <Tag
                      closable
                      onClose={clearDateRange}
                      className="manrope f-12 black-55 tag-filter"
                    >
                      Date Range:{" "}
                      <span className="manrope f-12 grey-77">
                        {startDate} - {endDate}
                      </span>
                    </Tag>
                  )}
                </div>
              </Col>
            </Row>
          )}

          {/* Tabs */}
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            className="product-tab library"
            tabBarGutter={16}
          >
            <TabPane
              tab={
                <span style={{ display: 'flex', alignItems: 'center', marginLeft: 0, paddingLeft: 0 }}>
                  <img src={ReviewPendingIcon} alt="" className="tab-icon pending-icon" style={{ marginLeft: 0 }} />
                  Pending Review ({counts.pending})
                </span>
              }
              key="pending"
            >
              <div style={{ paddingTop: 16 }}>
                <ResolutionRadioButtons tabName="pending" />
              </div>
              {filteredImages.length === 0 ? (
                <div className="empty-state">No images found</div>
              ) : (
                <div className="images-grid">
                  {filteredImages.map((image) => (
                    <ReviewImageCard
                      key={image.id}
                      image={image}
                      currentTab={activeTab}
                      currentUserRole={currentUserRole}
                      onCollaborate={handleCollaborate}
                      onViewRejectionHistory={handleViewRejectionHistory}
                      onRemoveFromReview={handleRemoveFromReview}
                      onRefresh={fetchReviewImages}
                    />
                  ))}
                </div>
              )}
            </TabPane>

            <TabPane
              tab={
                <span style={{ display: 'flex', alignItems: 'center', marginLeft: 0, paddingLeft: 0 }}>
                  <img src={ReviewAcceptedIcon} alt="" className="tab-icon approved-icon" style={{ marginLeft: 0 }} />
                  Approved ({counts.approved})
                </span>
              }
              key="approved"
            >
              <div style={{ paddingTop: 16 }}>
                <ResolutionRadioButtons tabName="approved" />
              </div>
              {filteredImages.length === 0 ? (
                <div className="empty-state">No images found</div>
              ) : (
                <div className="images-grid">
                  {filteredImages.map((image) => (
                    <ReviewImageCard
                      key={image.id}
                      image={image}
                      currentTab={activeTab}
                      currentUserRole={currentUserRole}
                      onCollaborate={handleCollaborate}
                      onViewRejectionHistory={handleViewRejectionHistory}
                      onRemoveFromReview={handleRemoveFromReview}
                      onRefresh={fetchReviewImages}
                    />
                  ))}
                </div>
              )}
            </TabPane>

            <TabPane
              tab={
                <span style={{ display: 'flex', alignItems: 'center', marginLeft: 0, paddingLeft: 0 }}>
                  <img src={ReviewRejectedIcon} alt="" className="tab-icon rejected-icon" style={{ marginLeft: 0 }} />
                  Rejected ({counts.rejected})
                </span>
              }
              key="rejected"
            >
              <div style={{ paddingTop: 16 }}>
                <ResolutionRadioButtons tabName="rejected" />
              </div>
              {filteredImages.length === 0 ? (
                <div className="empty-state">No images found</div>
              ) : (
                <div className="images-grid">
                  {filteredImages.map((image) => (
                    <ReviewImageCard
                      key={image.id}
                      image={image}
                      currentTab={activeTab}
                      currentUserRole={currentUserRole}
                      onCollaborate={handleCollaborate}
                      onViewRejectionHistory={handleViewRejectionHistory}
                      onRemoveFromReview={handleRemoveFromReview}
                      onRefresh={fetchReviewImages}
                    />
                  ))}
                </div>
              )}
            </TabPane>
          </Tabs>

          {/* Filter Drawer */}
          <Drawer
            width={360}
            visible={isFilterModal}
            onClose={handleFilterModalClose}
            placement="right"
            title={
              <Row>
                <Col span={2} className="justify-in-start">
                  <FilterOutlined />
                </Col>
                <Col span={22} className="justify-in-center">
                  Filters
                </Col>
              </Row>
            }
          >
            <div style={{ padding: 20 }}>
              <div style={{ marginBottom: 20 }}>
                <span
                  style={{ display: "block", marginBottom: 8 }}
                  className="manrope f-14 black-14"
                >
                  Date
                </span>
                <Select
                  style={{ width: 300, display: "block", marginBottom: 8 }}
                  className="tree-select-material f-14 filter bg-light manrope d-inline"
                  placeholder="All Time"
                  onChange={handleDateFilterChange}
                  size="large"
                >
                  <Select.Option value="all">All time</Select.Option>
                  <Select.Option value="last_30_days">Last 30 days</Select.Option>
                  <Select.Option value="month">This month</Select.Option>
                  <Select.Option value="last_month">Last month</Select.Option>
                  <Select.Option value="last_6_month">Last 6 months</Select.Option>
                  <Select.Option value="year">This year</Select.Option>
                  <Select.Option value="last_year">Last year</Select.Option>
                </Select>
              </div>
            </div>
          </Drawer>
        </div>
      </TwoDImagesContext.Provider>
    </CustomerMainLayout>
  );
};

const mapStateToProps = (state) => state;
const mapDispatchToProps = () => ({});

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(ReviewImagesPage)
);
